<template>
  <BaseLayout title="Student Detail">
    <div class="container my-4">
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else-if="notFound">
        <div class="alert alert-danger">Student not found.</div>
      </div>

      <div v-else-if="student">
        <!-- Header -->
        <div class="card mb-4">
          <div class="card-body d-flex align-items-center gap-3 flex-wrap">
            <div
              class="avatar-circle-lg d-flex align-items-center justify-content-center flex-shrink-0"
              style="background: #4f86c6;"
            >
              <span class="text-white fw-bold fs-5">{{ avatarLabel }}</span>
            </div>
            <div class="flex-grow-1">
              <h4 class="mb-0">{{ student.name || '(no name)' }}</h4>
              <span class="badge bg-secondary me-2">{{ student.uco }}</span>
              <!-- D62: IS MU profile link -->
              <a
                :href="`https://is.muni.cz/auth/osoba/${student.uco}`"
                target="_blank"
                rel="noopener noreferrer"
                class="badge bg-info text-dark text-decoration-none"
              >IS MU profile</a>
            </div>
            <button class="btn btn-outline-secondary btn-sm" @click="$router.push('/students')">
              ← Back
            </button>
          </div>
        </div>

        <!-- Stat tiles -->
        <div class="row g-2 mb-4" v-if="progressSummary">
          <div class="col-6 col-md-2">
            <div class="card text-center py-2">
              <div class="card-body p-1">
                <div class="fs-4 fw-bold">{{ homeworkCount }}</div>
                <div class="small text-muted">HOMEWORKS</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-2">
            <div class="card text-center py-2">
              <div class="card-body p-1">
                <div class="fs-4 fw-bold">{{ progressSummary.total_subtask_count }}</div>
                <div class="small text-muted">SUBTASKS</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-2">
            <div class="card text-center py-2">
              <div class="card-body p-1">
                <div class="fs-4 fw-bold text-success">{{ progressSummary.evaluated_subtask_count }}</div>
                <div class="small text-muted">EVALUATED</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-2">
            <div class="card text-center py-2">
              <div class="card-body p-1">
                <div class="fs-4 fw-bold text-warning">{{ progressSummary.manual_count }}</div>
                <div class="small text-muted">MANUAL</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-2">
            <div class="card text-center py-2">
              <div class="card-body p-1">
                <div class="fs-4 fw-bold">{{ student.submissions ? student.submissions.length : 0 }}</div>
                <div class="small text-muted">SUBMISSIONS</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <ul class="nav nav-tabs mb-3">
          <li class="nav-item">
            <button
              class="nav-link"
              :class="{ active: activeTab === 'overview' }"
              @click="activeTab = 'overview'"
            >Overview</button>
          </li>
          <li class="nav-item">
            <button
              class="nav-link"
              :class="{ active: activeTab === 'history' }"
              @click="activeTab = 'history'"
            >Evaluations &amp; Submissions</button>
          </li>
        </ul>

        <!-- Overview Tab -->
        <div v-if="activeTab === 'overview'">
          <div v-if="homeworkSections.length === 0" class="text-muted">No homeworks found.</div>
          <div
            v-for="hw in homeworkSections"
            :key="hw.homework_id"
            class="card mb-4"
          >
            <div class="card-header d-flex align-items-center gap-2 flex-wrap">
              <span class="badge bg-primary">HW{{ hwNumber(hw.homework_id) }}</span>
              <strong>{{ hw.homework_name }}</strong>
              <span class="text-muted small ms-auto">
                {{ progressForHw(hw) }} evaluated
              </span>
              <div class="progress ms-2" style="width:80px; height:8px;">
                <div
                  class="progress-bar"
                  :style="{ width: progressPct(hw) + '%' }"
                ></div>
              </div>
              <span v-if="hw.deadline_date" class="text-muted small">
                Deadline: {{ fmt(hw.deadline_date) }}
              </span>
            </div>
            <div class="card-body p-0">
              <table class="table table-sm mb-0">
                <thead class="table-light">
                  <tr>
                    <th>SUBTASK</th>
                    <th>RESULT</th>
                    <th>EVALUATION</th>
                    <th>DURATION</th>
                    <th>LAST SUBMISSION</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in hw.rows" :key="row.subtask_id">
                    <td>{{ stNumber(hw, row) }}. {{ row.subtask_name }}</td>
                    <td>
                      <span v-if="row.passed === true" class="text-success fw-bold">✓ Pass</span>
                      <span v-else-if="row.passed === false" class="text-danger fw-bold">✗ Fail</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <span
                        v-if="statusBadge(row.eval, row.is_manual)"
                        :class="['badge', statusBadge(row.eval, row.is_manual).cls]"
                      >{{ statusBadge(row.eval, row.is_manual).label }}{{ row.is_manual && row.manually_evaluated_at ? ' (' + fmtShort(row.manually_evaluated_at) + ')' : '' }}</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <span v-if="row.eval && row.eval.duration_seconds != null">
                        {{ row.eval.duration_seconds }}s
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <span v-if="row.submission">
                        {{ fmt(row.submission.submitted_at) }}
                        <span class="text-muted small">{{ row.submission.file_count }} file(s)</span>
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td class="text-end">
                      <button
                        class="btn btn-outline-secondary btn-sm me-1"
                        :disabled="!hw.deadline_id || !row.submission"
                        @click="rerunSubtask(row, hw)"
                        title="Re-run evaluation"
                      >Re-run</button>
                      <button
                        class="btn btn-outline-warning btn-sm"
                        :disabled="!row.submission || !hw.deadline_id"
                        @click="openManual(hw)"
                        title="Set manual result"
                      >Manual</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Evaluations & Submissions Tab -->
        <div v-if="activeTab === 'history'">
          <!-- Evaluations table -->
          <h6 class="fw-semibold mb-2">Evaluations</h6>
          <div v-if="!student.evaluations || student.evaluations.length === 0" class="text-muted mb-4">
            No evaluations.
          </div>
          <table v-else class="table table-sm mb-4">
            <thead class="table-light">
              <tr>
                <th>SUBTASK</th>
                <th>TYPE</th>
                <th>STATUS</th>
                <th>DURATION</th>
                <th>AT</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="ev in student.evaluations" :key="ev.evaluation_id">
                <tr v-for="se in ev.subevaluations" :key="se.subtask_id + '-' + ev.evaluation_id">
                  <td>{{ hwLabel(ev.homework_id) }}·{{ se.subtask_name }}</td>
                  <td>
                    <span :class="ev.is_manual ? 'badge bg-warning text-dark' : 'badge bg-secondary'">
                      {{ ev.is_manual ? 'Manual' : 'Auto' }}
                    </span>
                  </td>
                  <td>
                    <span
                      v-if="statusBadge(ev, ev.is_manual)"
                      :class="['badge', statusBadge(ev, ev.is_manual).cls]"
                    >{{ statusBadge(ev, ev.is_manual).label }}{{ ev.is_manual && ev.manually_evaluated_at ? ' (' + fmtShort(ev.manually_evaluated_at) + ')' : '' }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span v-if="ev.duration_seconds != null">{{ ev.duration_seconds }}s</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span v-if="ev.evaluation_ended">{{ fmt(ev.evaluation_ended) }}</span>
                    <span v-else-if="ev.manually_evaluated_at">{{ fmt(ev.manually_evaluated_at) }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <!-- Submissions table -->
          <h6 class="fw-semibold mb-2">Submissions</h6>
          <div v-if="!student.submissions || student.submissions.length === 0" class="text-muted">
            No submissions.
          </div>
          <table v-else class="table table-sm">
            <thead class="table-light">
              <tr>
                <th>SUBTASK</th>
                <th>FILES</th>
                <th>SUBMITTED</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sub in student.submissions" :key="sub.submission_id">
                <td>{{ sub.subtask_name }}</td>
                <td>{{ sub.file_count }}</td>
                <td>{{ fmt(sub.submitted_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- PassFailModal (D40: shared with F8 — available after F8 rebase) -->
    <PassFailModal
      v-if="manualTarget"
      ref="passFailModal"
      :student="{ uco: student ? student.uco : 0, name: student ? student.name : '', id: student ? student.student_id : 0 }"
      :subtasks="manualTarget.subtasks"
      :deadline-id="manualTarget.deadline_id"
      @saved="onManualSaved"
    />

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import StemErrorModal from '@/components/StemErrorModal.vue';
import PassFailModal from '@/components/PassFailModal.vue';
import StudentController from '@/controllers/StudentController';
import EvaluationController from '@/controllers/EvaluationController';
import { mergeData } from '@/models/student';
import { mapGetters } from 'vuex';
import { Modal } from 'bootstrap';

export default {
  name: 'StudentTeacherView',
  components: { BaseLayout, StemErrorModal, PassFailModal },
  props: {
    uco: { type: Number, required: true },
  },
  data() {
    return {
      student: null,
      notFound: false,
      progressSummary: null,
      homeworkSections: [],
      activeTab: 'overview',
      manualTarget: null,
      loading: true,
    };
  },
  computed: {
    ...mapGetters(['semesterId']),
    avatarLabel() {
      return this.student ? 'S' : '?';
    },
    homeworkCount() {
      return this.student ? (this.student.homeworks || []).length : 0;
    },
  },
  async created() {
    await this.loadAll();
  },
  methods: {
    // D45: loadAll() instead of this.$options.mounted.call(this)
    async loadAll() {
      this.loading = true;
      try {
        const [ctxResult, summaryResult] = await Promise.allSettled([
          StudentController.fetchStudentWithContext(this.uco, this.semesterId),
          StudentController.fetchProgressSummary(this.uco, this.semesterId),
        ]);

        if (ctxResult.status === 'rejected') {
          if (ctxResult.reason?.response?.status === 404) {
            this.notFound = true;
            this.student = null;
          } else {
            this.$refs.stemErrorModal.show('Error', ctxResult.reason.message);
          }
          return;
        }

        if (summaryResult.status === 'rejected') {
          this.$refs.stemErrorModal.show('Error', summaryResult.reason.message);
        } else {
          this.progressSummary = summaryResult.value;
        }

        const ctx = ctxResult.value;
        this.notFound = false;
        this.student = ctx;
        this.homeworkSections = mergeData(ctx.homeworks, ctx.evaluations, ctx.submissions);
      } finally {
        this.loading = false;
      }
    },

    // B26: Re-run with deadline_id and single subtask_id
    async rerunSubtask(row, hw) {
      try {
        await EvaluationController.startEvaluation({
          homework_id: hw.homework_id,
          subtask_ids: [row.subtask_id],
          student_ucos: [String(this.student.uco)],
          all_students: false,
          deadline_id: row.eval?.deadline_id ?? hw.deadline_id,
          hard_evaluation: true,
        });
        await this.loadAll();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },

    openManual(hw) {
      this.manualTarget = {
        deadline_id: hw.deadline_id,
        subtasks: hw.rows.filter(r => r.submission).map(r => ({ id: r.subtask_id, title: r.subtask_name })),
      };
      this.$nextTick(() => {
        const el = this.$refs.passFailModal?.$el;
        if (el) {
          const modal = new Modal(el);
          modal.show();
          el.addEventListener('hidden.bs.modal', () => { this.manualTarget = null; }, { once: true });
        }
      });
    },

    async onManualSaved() {
      this.manualTarget = null;
      await this.loadAll();
    },

    statusBadge(ev, isManual) {
      if (ev?.container_error) return { cls: 'bg-danger', label: 'Error' };
      if (ev?.in_queue) return { cls: 'bg-info text-dark', label: 'In queue' };
      if (ev?.canceled) return { cls: 'bg-secondary', label: 'Canceled' };
      if (isManual) return { cls: 'bg-warning text-dark', label: 'Manual' };
      if (ev?.evaluation_ended) return { cls: 'bg-success', label: 'Evaluated' };
      return null;
    },

    // Formatting helpers
    fmt(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' })
        + '·' + d.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' });
    },
    fmtShort(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('sk-SK', { day: 'numeric', month: 'numeric' });
    },
    hwNumber(homeworkId) {
      const idx = (this.student?.homeworks || []).findIndex(h => h.homework_id === homeworkId);
      return String(idx + 1).padStart(2, '0');
    },
    hwLabel(homeworkId) {
      return 'HW' + this.hwNumber(homeworkId);
    },
    stNumber(hw, row) {
      const hwIdx = (this.student?.homeworks || []).findIndex(h => h.homework_id === hw.homework_id);
      const stIdx = hw.rows.findIndex(r => r.subtask_id === row.subtask_id);
      return `${hwIdx + 1}.${stIdx + 1}`;
    },
    progressForHw(hw) {
      const evaluated = hw.rows.filter(r => r.passed !== null).length;
      return `${evaluated}/${hw.rows.length}`;
    },
    progressPct(hw) {
      if (!hw.rows.length) return 0;
      return Math.round(hw.rows.filter(r => r.passed !== null).length / hw.rows.length * 100);
    },
  },
};
</script>

<style scoped>
.avatar-circle-lg {
  width: 56px;
  height: 56px;
  border-radius: 50%;
}
</style>
